import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  REWARDS, REWARD_IDS, buildPendingMatch, canUseCard, clamp, getCareerStage, getRelicEffectAmount, getStageBoss, getStageBossBalance, getWorldCup2026RoutePhaseTitle,
  effectiveRating, getActiveRelicSynergies, matchOutlook, relicSynergyRecipe,
  type CardRewardId, type EncounterKind, type GameStateV2, type PendingMatch, type RewardId,
} from '../game'
import { ClubCrest } from './ClubCrest'
import { appearanceProbability } from '../match'
import './MatchPrepDialog.css'

export const FIRST_MATCH_CONSUMABLE_TUTORIAL_KEY = 'football-roguelike-match-consumable-tutorial-v1'

export function hasSeenFirstMatchConsumableTutorial(storage: Pick<Storage, 'getItem'>): boolean {
  return storage.getItem(FIRST_MATCH_CONSUMABLE_TUTORIAL_KEY) !== null
}

export function markFirstMatchConsumableTutorialSeen(storage: Pick<Storage, 'setItem'>): void {
  storage.setItem(FIRST_MATCH_CONSUMABLE_TUTORIAL_KEY, 'seen')
}

interface MatchPrepDialogProps {
  state: GameStateV2
  encounter: EncounterKind
  opponent: string
  onConfirm: (cardId: CardRewardId | null) => void
}

function preMatchCardCopy(cardId: CardRewardId): string {
  const effect = REWARDS[cardId].effect
  if (effect.type === 'assistChance') return '+ asistencias'
  if (effect.type === 'physicalRestore') return '+ físico'
  if (effect.type === 'moraleRestore') return '+ moral'
  if (effect.type === 'ignorePhysicalPenalty') return 'Ignora el desgaste'
  return 'Mejora este partido'
}

interface BuildCondition {
  id: RewardId
  name: string
  detail: string
  status: 'active' | 'ready' | 'inactive'
  statusLabel: string
}

function buildCondition(state: GameStateV2, pending: PendingMatch, rewardId: RewardId): BuildCondition | null {
  const reward = REWARDS[rewardId]
  if (reward.kind !== 'relic' || (state.inventory[rewardId]?.count ?? 0) < 1) return null
  const safety = REWARDS['segundo-pulmon'].effect
  const safetyRecovery = pending.secondLungTriggered && safety.type === 'physicalSafety' ? safety.amount : 0
  const physical = clamp(state.physical + (pending.physicalRecovery ?? 0) - pending.physicalCost + safetyRecovery)
  const morale = clamp(state.morale + (pending.moraleRecovery ?? 0))
  const effect = reward.effect

  switch (effect.type) {
    case 'moraleBonus': {
      const copies = state.inventory[rewardId]?.count ?? 0
      const ratingBonus = Math.min(effect.maximumRatingBonus ?? 0, copies * (effect.ratingAmount ?? 0))
      const active = effect.threshold !== undefined && morale >= effect.threshold && ratingBonus > 0
      return { id: rewardId, name: reward.name, detail: `+${effect.amount * copies} moral tras jugar · +${ratingBonus} OVR efectivo con moral ${effect.threshold ?? 0}+`, status: active ? 'active' : 'ready', statusLabel: active ? 'Activa' : `Moral ${morale}/${effect.threshold ?? 0}` }
    }
    case 'highPhysicalRating': {
      const active = physical >= effect.threshold
      return { id: rewardId, name: reward.name, detail: `+${effect.amount} OVR efectivo · físico previsto ${physical}/${effect.threshold}`, status: active ? 'active' : 'inactive', statusLabel: active ? 'Activa' : 'No se activa' }
    }
    case 'physicalSafety': {
      const used = state.relicUses?.['segundo-pulmon'] === state.season
      return { id: rewardId, name: reward.name, detail: `+${effect.amount} físico al caer bajo ${effect.threshold}`, status: pending.secondLungTriggered ? 'active' : used ? 'inactive' : 'ready', statusLabel: pending.secondLungTriggered ? 'Se activa ahora' : used ? 'Ya utilizado' : 'En reserva' }
    }
    case 'postRestCostReduction':
      return { id: rewardId, name: reward.name, detail: `−${effect.amount} al coste físico de este encuentro`, status: pending.restPreparationTriggered ? 'active' : 'inactive', statusLabel: pending.restPreparationTriggered ? 'Activa' : 'Sin descanso previo' }
    case 'lowMoraleRating': {
      const active = morale < effect.threshold
      return { id: rewardId, name: reward.name, detail: `+${effect.amount} OVR efectivo · moral prevista ${morale}`, status: active ? 'active' : 'inactive', statusLabel: active ? 'Activa' : `Requiere moral < ${effect.threshold}` }
    }
    case 'physicalPenaltyCap': {
      const active = physical < effect.threshold
      return { id: rewardId, name: reward.name, detail: `Penalización física máxima de −${effect.amount} · físico previsto ${physical}`, status: active ? 'active' : 'inactive', statusLabel: active ? 'Activa' : `Requiere físico < ${effect.threshold}` }
    }
    case 'lowMoraleLossRecovery':
      return { id: rewardId, name: reward.name, detail: `+${effect.amount} moral si una derrota te deja bajo ${effect.threshold}`, status: 'ready', statusLabel: 'En reserva' }
    case 'streakRating': {
      const bonus = Math.min(effect.maximum, state.winStreak * effect.amount)
      return { id: rewardId, name: reward.name, detail: `+${bonus} OVR efectivo · racha ${state.winStreak}`, status: bonus > 0 ? 'active' : 'inactive', statusLabel: bonus > 0 ? 'Activa' : 'Sin racha' }
    }
    case 'streakPhysicalRecovery': {
      const ready = state.winStreak + 1 >= effect.streak
      return { id: rewardId, name: reward.name, detail: `+${effect.amount} físico por victoria con racha ${effect.streak}+`, status: ready ? 'ready' : 'inactive', statusLabel: ready ? 'Se activa si ganas' : `Racha ${state.winStreak}/${effect.streak}` }
    }
    default:
      return null
  }
}

export function MatchPrepDialog({ state, encounter, opponent, onConfirm }: MatchPrepDialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const backdropRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const cardPickerRef = useRef<HTMLFieldSetElement>(null)
  const [selectedCard, setSelectedCard] = useState<CardRewardId | null>(null)
  const [resolving, setResolving] = useState(false)
  const [showConsumableTutorial, setShowConsumableTutorial] = useState(() => !hasSeenFirstMatchConsumableTutorial(localStorage))

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    const pageScroller = document.scrollingElement ?? document.documentElement
    const previousPageScroll = pageScroller.scrollTop
    document.body.style.overflow = 'hidden'
    const frame = requestAnimationFrame(() => {
      const dialog = dialogRef.current
      if (!dialog) return
      pageScroller.scrollTop = 0
      if (backdropRef.current) backdropRef.current.scrollTop = 0
      dialog.scrollTop = 0
      if (showConsumableTutorial) dialog.querySelector<HTMLElement>('[aria-label="Cerrar tutorial de consumibles"]')?.focus()
      else dialog.focus({ preventScroll: true })
    })
    const trap = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); return }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const items = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled)')]
      if (!items.length) return
      if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1)?.focus() }
      else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0].focus() }
    }
    document.addEventListener('keydown', trap)
    return () => { cancelAnimationFrame(frame); document.body.style.overflow = previousOverflow; pageScroller.scrollTop = previousPageScroll; document.removeEventListener('keydown', trap); opener?.focus({ preventScroll: true }) }
  }, [])

  useEffect(() => {
    if (showConsumableTutorial) markFirstMatchConsumableTutorialSeen(localStorage)
  }, [showConsumableTutorial])

  const pending = buildPendingMatch(state, encounter, selectedCard)
  const outlook = matchOutlook(pending.winProbability)
  const outlookContent = {
    favorable: { label: 'Favorable', copy: 'Tu preparación inclina el encuentro a tu favor.', icon: 'mdi:trending-up' },
    even: { label: 'Igualado', copy: 'El partido se decidirá por detalles.', icon: 'mdi:approximately-equal' },
    difficult: { label: 'Complicado', copy: 'Necesitarás una actuación por encima de lo esperado.', icon: 'mdi:alert-outline' },
  }[outlook]
  const compatibleCards = REWARD_IDS.filter((id): id is CardRewardId => REWARDS[id].kind === 'card' && canUseCard(state.inventory, id as CardRewardId, encounter))
  const hiddenIncompatible = REWARD_IDS.filter((id) => REWARDS[id].kind === 'card' && (state.inventory[id]?.count ?? 0) > 0 && !canUseCard(state.inventory, id as CardRewardId, encounter)).length
  const buildConditions = REWARD_IDS.map((id) => buildCondition(state, pending, id)).filter((condition): condition is BuildCondition => condition !== null)
  const activeSynergies = getActiveRelicSynergies(state.inventory)
  const projectedPhysical = pending.projectedPhysical ?? clamp(state.physical - pending.physicalCost + (pending.physicalRecovery ?? 0))
  const projectedMorale = pending.projectedMorale ?? clamp(state.morale + (pending.moraleRecovery ?? 0))
  const baseEffectiveRating = effectiveRating({ ...state, physical: projectedPhysical, morale: projectedMorale })
  const ratingDelta = (pending.effectiveRating ?? baseEffectiveRating) - baseEffectiveRating
  const stage = getCareerStage(state.season, state.finaleKind)
  const stageBoss = getStageBoss(state.season, state.club, state.leagueMovements, state.finaleKind)
  const bossRound = stageBoss.rounds[state.boss?.round ?? 0] ?? stageBoss.rounds[0]
  const isWorldCupFinale = state.season === 5 && state.finaleKind === 'world-cup'
  const isWorldCupPhase = isWorldCupFinale && encounter === 'match'
  const isLeagueBlock = encounter === 'match' && !isWorldCupPhase
  const isBoss = encounter === 'boss'
  const isDerby = encounter === 'derby'
  const isCardsOnly = true
  const bossRoundIndex = state.boss?.round ?? 0
  const bossScores = state.boss?.scores ?? []
  const bossIntermissionRecovery = getStageBossBalance(state.season).intermissionPhysicalRecovery + Math.floor(getRelicEffectAmount(state.inventory, 'restPhysicalBonus') / 2)
  const bossHome = isWorldCupFinale ? state.player.country : state.club
  const worldCupPhase = getWorldCup2026RoutePhaseTitle(state.player.country, state.week)
  const encounterTitle = encounter === 'boss' ? bossRound : isDerby ? 'El derbi' : isWorldCupPhase ? worldCupPhase : 'Tramo de liga'
  const encounterEyebrow = encounter === 'boss' ? stageBoss.eyebrow : isDerby ? 'Rivalidad máxima' : isLeagueBlock ? 'Preparación de la build' : 'Antes del partido'
  const encounterDescription = encounter === 'boss'
    ? stageBoss.description
    : isWorldCupPhase && state.week === 0
      ? 'Simulas tres partidos de la fase de grupos. Necesitas ganar al menos dos para clasificar; con cero o una victoria, la selección queda eliminada.'
      : isWorldCupPhase
        ? 'Eliminatoria a partido único: una derrota termina la carrera y una victoria mantiene vivo el Mundial.'
    : encounter === 'match'
      ? 'Este nodo simula varios partidos. Tu prestigio en el club decide si participas y tu build determina el rendimiento; puedes gastar un consumible para alterar el tramo.'
      : 'Elige un consumible si lo necesitas.'
  const confirm = () => {
    if (resolving) return
    setResolving(true)
    onConfirm(selectedCard)
  }
  const closeConsumableTutorial = () => {
    setShowConsumableTutorial(false)
    requestAnimationFrame(() => {
      const nextAction = cardPickerRef.current?.querySelector<HTMLElement>('[role="radio"]')
        ?? dialogRef.current?.querySelector<HTMLElement>('.match-setup__footer button')
      nextAction?.focus()
    })
  }

  return (
    <div ref={backdropRef} className="dialog-backdrop match-setup-backdrop">
      <section ref={dialogRef} className={`match-setup${isCardsOnly ? ' match-setup--cards-only' : ''}${isBoss ? ' match-setup--boss' : ''}${isDerby ? ' match-setup--derby' : ''}${isLeagueBlock ? ' match-setup--league' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} tabIndex={-1}>
        <header className="match-setup__header">
          <div><p className="eyebrow">{encounterEyebrow}</p><h2 id={titleId}>{encounterTitle}</h2><p id={descriptionId}>{encounterDescription}</p></div>
          <span className={`match-setup__number${isDerby ? ' match-setup__number--derby' : ''}`} aria-hidden="true">{isDerby ? <Icon icon="mdi:fire" /> : String(state.week + 1).padStart(2, '0')}</span>
        </header>

        {isLeagueBlock && <aside className="squad-role" aria-label="Probabilidad de participación">
          <Icon icon="mdi:account-clock-outline" aria-hidden="true" />
          <span><small>Prestigio en {state.club}</small><strong>{state.clubReputation}/100 · {Math.round(appearanceProbability(state.clubReputation) * 100)}% de jugar cada partido</strong></span>
        </aside>}

        {(isBoss || isDerby) && <>
          <section className={`boss-showdown${isDerby ? ' boss-showdown--derby' : ''}`} aria-label={`${bossHome} contra ${opponent}`}>
            <div className="boss-showdown__team boss-showdown__team--home">
              <ClubCrest name={bossHome} size="large" />
              <span><small>{isWorldCupFinale ? 'Tu selección' : 'Tu equipo'}</small><strong title={bossHome}>{bossHome}</strong></span>
            </div>
            <div className="boss-showdown__versus" aria-hidden="true"><Icon icon={isDerby ? 'mdi:fire' : isWorldCupFinale ? 'mdi:earth' : 'mdi:trophy-variant-outline'} /><small>{isDerby ? 'Partido único' : `Ronda ${bossRoundIndex + 1} de ${stageBoss.rounds.length}`}</small><strong>VS</strong></div>
            <div className="boss-showdown__team boss-showdown__team--away">
              <ClubCrest name={opponent} size="large" />
              <span><small>{isDerby ? 'El otro lado' : 'El rival'}</small><strong title={opponent}>{opponent}</strong></span>
            </div>
          </section>
          {isBoss && <section className={`boss-series boss-series--rounds-${stageBoss.rounds.length}`} aria-label={`Progreso de ${stageBoss.name}`}>
            <header><span>{stageBoss.name}</span><strong>Necesitas {stageBoss.requiredWins} victoria{stageBoss.requiredWins === 1 ? '' : 's'}</strong></header>
            {bossIntermissionRecovery > 0 && bossRoundIndex < stageBoss.rounds.length - 1 && <p className="boss-series__recovery"><Icon icon="mdi:heart-pulse" aria-hidden="true" /> {stageBoss.eliminationOnLoss ? 'Si avanzas, recuperas' : 'Recuperas'} {bossIntermissionRecovery} de físico antes de la siguiente ronda</p>}
            <ol>
              {stageBoss.rounds.map((round, index) => {
                const result = bossScores[index]
                const status = result === 'win' ? 'is-win' : result === 'loss' ? 'is-loss' : index === bossRoundIndex ? 'is-current' : 'is-pending'
                return <li className={status} key={round}><span>{result === 'win' ? <Icon icon="mdi:check" /> : result === 'loss' ? <Icon icon="mdi:close" /> : index + 1}</span><small>{round}</small></li>
              })}
            </ol>
          </section>}
        </>}

        {activeSynergies.length > 0 && <section className="matchup-report" aria-label="Sinergias entre reliquias">
          <Icon icon="mdi:link-variant" aria-hidden="true" />
          <span><strong>{activeSynergies.map((synergy) => synergy.name).join(' · ')}</strong><small>{activeSynergies.map(relicSynergyRecipe).join(' · ')}</small></span>
        </section>}

        <aside className={`match-outlook match-outlook--${outlook}`} aria-live="polite" aria-label={`Lectura del encuentro: ${outlookContent.label}`}>
          <Icon icon={outlookContent.icon} aria-hidden="true" />
          <span><small>Lectura del encuentro</small><strong>{outlookContent.label}</strong><em>{outlookContent.copy}</em></span>
        </aside>

        <dl className="match-projection" aria-label="Proyección del encuentro">
          <div><dt>Físico proyectado</dt><dd>{state.physical} → {projectedPhysical}</dd></div>
          <div><dt>Moral proyectada</dt><dd>{state.morale} → {projectedMorale}</dd></div>
          <div><dt>OVR efectivo</dt><dd>{pending.effectiveRating ?? baseEffectiveRating}<small>{ratingDelta === 0 ? 'Sin cambio' : `${ratingDelta > 0 ? '+' : ''}${ratingDelta} por la selección`}</small></dd></div>
        </dl>

        {showConsumableTutorial && <aside className="match-item-tutorial" role="note" aria-label="Cómo seleccionar consumibles">
          <Icon icon="mdi:gesture-tap-button" aria-hidden="true" />
          <span>
            <strong>Elige antes de jugar</strong>
            <p>{compatibleCards.length > 0
              ? 'Elige una carta disponible en la lista. La marcada se consume al jugar; usa «Sin consumible» para guardarlas.'
              : 'Ahora no tienes consumibles compatibles. Cuando consigas uno aparecerá aquí: selecciónalo antes de jugar y se consumirá al usarlo.'}</p>
          </span>
          <button type="button" aria-label="Cerrar tutorial de consumibles" onClick={closeConsumableTutorial}><Icon icon="mdi:check" aria-hidden="true" /> Entendido</button>
        </aside>}

        {(compatibleCards.length > 0 || hiddenIncompatible > 0 || buildConditions.length > 0) && <fieldset ref={cardPickerRef} className="card-picker">
          {compatibleCards.length > 0 && <>
            <legend><span>1</span> Consumible <small>opcional</small></legend>
            <div role="radiogroup" aria-label={isLeagueBlock ? 'Consumible para este tramo' : 'Consumible para este partido'}>
              <button className={selectedCard === null ? 'is-active' : ''} type="button" role="radio" aria-checked={selectedCard === null} onClick={() => setSelectedCard(null)}>
                <Icon icon="mdi:bag-personal-outline" /><span><strong>Sin consumible</strong><small>Conservar</small></span>
              </button>
              {compatibleCards.map((id) => {
                const reward = REWARDS[id]
                return <button className={selectedCard === id ? 'is-active' : ''} key={id} type="button" role="radio" aria-checked={selectedCard === id} onClick={() => setSelectedCard(id)}><Icon icon="mdi:cards-playing-outline" /><span><strong>{reward.name} <em>×{state.inventory[id]?.count}</em></strong><small>{preMatchCardCopy(id)}</small></span></button>
              })}
            </div>
          </>}
          {hiddenIncompatible > 0 && <p>{hiddenIncompatible} consumible{hiddenIncompatible > 1 ? 's' : ''} reservado{hiddenIncompatible > 1 ? 's' : ''} para otro tipo de encuentro.</p>}
          {buildConditions.length > 0 && <section className="build-conditions" aria-label="Condicionales">
            <header><Icon icon="mdi:function-variant" aria-hidden="true" /><span><strong>Condicionales</strong></span></header>
            <ul>
              {buildConditions.map((condition) => <li className={`is-${condition.status}`} key={condition.id}>
                <Icon icon={condition.status === 'active' ? 'mdi:check-circle' : condition.status === 'ready' ? 'mdi:clock-outline' : 'mdi:circle-outline'} aria-hidden="true" />
                <span><strong>{condition.name}</strong><small>{condition.detail}</small></span>
                <em>{condition.statusLabel}</em>
              </li>)}
            </ul>
          </section>}
        </fieldset>}

        <footer className={`match-setup__footer${isCardsOnly ? ' match-setup__footer--simple' : ''}`}>
          <button className="button button--primary" type="button" disabled={resolving} onClick={confirm}>{resolving ? 'Preparando…' : isBoss ? 'Disputar ronda' : isWorldCupPhase && state.week === 0 ? 'Simular fase de grupos' : isWorldCupPhase ? 'Jugar eliminatoria' : encounter === 'match' ? 'Simular tramo' : 'Jugar partido'} <Icon icon="mdi:arrow-right" /></button>
        </footer>
      </section>
    </div>
  )
}
